import React, { useState, useEffect } from 'react';
import { loadBranding } from '../data/storage';

const DISMISS_KEY = 'ic_ann_v2_dismissed';

const COLOR_MAP = {
  gold:  { bg:'#b8860b', text:'#fff' },
  navy:  { bg:'#1a2a4a', text:'#fff' },
  green: { bg:'#1a7a4a', text:'#fff' },
  red:   { bg:'#c0392b', text:'#fff' },
};

function WAIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink:0 }}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}

function fmtGroupDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { weekday:'long', day:'numeric', month:'long' });
}

// Returns true if the banner should be shown based on date logic
function shouldShowByDate(ann) {
  if (!ann.groupDate) return true; // no date = always show while active
  const now = new Date();
  const groupDate = new Date(ann.groupDate);
  const showFrom  = new Date(groupDate);
  showFrom.setDate(showFrom.getDate() - 7);
  if (now < showFrom)   return false; // too early
  if (now >= groupDate) return false; // group started, hide
  return true;
}

// tier: 'trial' | 'standard' | 'premium' | null (visitor)
export default function AnnouncementBanner({ candidate }) {
  const [ann,       setAnn]       = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const branding = loadBranding();
    setAnn(branding.announcement || null);
    try {
      if (sessionStorage.getItem(DISMISS_KEY)) setDismissed(true);
    } catch {}
  }, []);

  function dismiss() {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch {}
  }

  if (!ann || !ann.active || dismissed) return null;
  if (!shouldShowByDate(ann)) return null;

  // showTo logic
  const tier = candidate?.tier || null;
  const showTo = ann.showTo || 'visitors_and_trial';
  if (showTo === 'visitors_and_trial') {
    // Hide for paid students and teachers
    if (tier && tier !== 'trial') return null;
  }
  // 'everyone' — show to all

  const scheme = COLOR_MAP[ann.color] || COLOR_MAP.gold;
  const dateLabel = fmtGroupDate(ann.groupDate);
  const displayMsg = ann.groupDate
    ? `New IELTS group starting ${dateLabel} — limited places available!`
    : ann.message;
  const waText = `Hello Mr. Ahmed Salem, I would like to reserve my place in the new IELTS group${dateLabel ? ` starting ${dateLabel}` : ''}. Please contact me with details.`;

  return (
    <>
      <style>{`
        @keyframes bellRing {
          0%,100%{transform:rotate(0)} 15%{transform:rotate(15deg)} 30%{transform:rotate(-12deg)}
          45%{transform:rotate(8deg)} 60%{transform:rotate(-5deg)} 75%{transform:rotate(3deg)}
        }
        .bell-ring { display:inline-block; animation: bellRing 2.5s ease-in-out infinite; transform-origin: 50% 0; }
      `}</style>
      <div style={{
        background: scheme.bg,
        color: scheme.text,
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap',
        zIndex: 150,
        position: 'relative',
      }}>
        {/* Animated bell */}
        <span className="bell-ring" style={{ fontSize: 18, flexShrink: 0 }}>🔔</span>

        {/* Message */}
        <div style={{ flex: 1, fontSize: 13.5, fontWeight: 600, lineHeight: 1.5, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
          {displayMsg}
        </div>

        {/* WA Reserve button */}
        <a href={`https://wa.me/201008182425?text=${encodeURIComponent(waText)}`}
          target="_blank" rel="noreferrer"
          style={{
            background: '#25d366', color: '#fff',
            border: 'none', padding: '7px 14px',
            borderRadius: 8, fontSize: 12.5, fontWeight: 700,
            cursor: 'pointer', textDecoration: 'none',
            display: 'flex', alignItems: 'center', gap: 6,
            flexShrink: 0, fontFamily: "'Plus Jakarta Sans',sans-serif",
            boxShadow: '0 2px 8px rgba(37,211,102,0.35)',
          }}>
          <WAIcon/> Reserve My Place
        </a>

        {/* Dismiss */}
        <button onClick={dismiss} style={{
          background: 'rgba(0,0,0,0.18)', border: 'none', color: '#fff',
          width: 26, height: 26, borderRadius: '50%', cursor: 'pointer',
          fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, lineHeight: 1,
        }} aria-label="Dismiss">×</button>
      </div>
    </>
  );
}
